
import React, { useState, useCallback, useEffect } from "react";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { NursingHomeLeads } from "@/components/dashboard/NursingHomeLeads";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import { useIsMobile } from "@/hooks/use-mobile";
import { Loader2, AlertCircle } from "lucide-react";

export default function Dashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { nursingHomeLeads, isLoading, error, refetch } = useDashboardData(user, refreshTrigger);
  const isMobile = useIsMobile();

  // Function to trigger data refresh
  const handleLeadUpdate = useCallback(() => {
    console.log("Updating leads...");
    // Increment refresh trigger to force data refetch
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Check for newly verified user flag
  useEffect(() => {
    const justVerified = sessionStorage.getItem('justVerified');
    if (justVerified && user) {
      // Clear the flag
      sessionStorage.removeItem('justVerified');
      
      // Trigger data refresh to ensure we have the latest leads
      handleLeadUpdate();
    }
  }, [user, handleLeadUpdate]);

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth/login?redirectTo=/dashboard" />;
  }

  return (
    <Layout>
      <Helmet>
        <title>My Dashboard | Grandpal</title>
      </Helmet>

      <div className="container py-4 md:py-8 max-w-7xl px-3 md:px-4">
        <DashboardHeader user={user} />
        
        {isLoading || authLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center">
              <Loader2 className="h-10 w-10 text-grandpal-green animate-spin mb-4" />
              <p className="text-gray-600">Loading your dashboard...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-600 mb-4">We encountered an issue while loading your dashboard data.</p>
            <button 
              onClick={refetch}
              className="px-4 py-2 bg-grandpal-green text-white rounded-md hover:bg-grandpal-green-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:gap-6 mt-4">
            <NursingHomeLeads 
              leads={nursingHomeLeads} 
              onLeadUpdate={handleLeadUpdate} 
            />
            
            {nursingHomeLeads && nursingHomeLeads.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Need help?</strong> If you're having trouble seeing all your selected nursing homes, 
                  please contact us at <a href="mailto:hello@updates.grandpal.co" className="underline">hello@updates.grandpal.co</a>.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
